import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  TextField,
  Chip,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FlagIcon from '@mui/icons-material/Flag';
import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import api from '../../api/config';
import { STATUSES } from '../../api/endpoints';
import { extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { buildStatusListParams, STATUS_MODEL_OPTIONS } from './statusApiHelpers';
import { STATUSES_CREATE_PATH } from '../../navigations/path';

const StatusList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [modelInput, setModelInput] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(STATUSES.LIST, {
        params: buildStatusListParams({
          page: pagination.page,
          perPage: pagination.per_page,
          search,
          model: modelFilter,
        }),
      });
      const list = data?.data?.list ?? data?.list ?? (Array.isArray(data?.data) ? data.data : []);
      setRows(Array.isArray(list) ? list : []);
      const pag = extractPagination(data);
      setPagination((p) => ({ ...p, total: pag.total ?? list.length ?? 0 }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load statuses');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search, modelFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
    setModelFilter((modelInput || '').trim());
  };

  const handleClear = () => {
    setSearchInput('');
    setModelInput(null);
    setSearch('');
    setModelFilter('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const colCount = 5;

  const renderColorCell = (color) => {
    if (!color) return '—';
    return (
      <Chip
        label={color}
        size="small"
        sx={{
          bgcolor: color,
          color: 'common.white',
          border: '1px solid',
          borderColor: 'divider',
          '& .MuiChip-label': { color: 'inherit' },
        }}
      />
    );
  };

  const renderIconCell = (icon) => {
    if (!icon) return '—';
    return (
      <Chip
        icon={<FlagIcon fontSize="small" />}
        label={icon}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FlagIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Statuses
            </Typography>
          </Box>
          <Can permissions={['status-create', 'status-view']}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(STATUSES_CREATE_PATH)}
            >
              Create status
            </Button>
          </Can>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search statuses..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={STATUS_MODEL_OPTIONS}
            value={modelInput}
            onChange={(_, v) => setModelInput(v ?? '')}
            onInputChange={(_, v) => setModelInput(v)}
            sx={{ minWidth: 220 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by model"
                placeholder="e.g. Equipment, Lead"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            )}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="text" onClick={handleClear}>
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Color</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Icon</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No statuses found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {row.name ?? '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={row.model ?? '—'} size="small" variant="outlined" color="primary" />
                        </TableCell>
                        <TableCell>{renderColorCell(row.color)}</TableCell>
                        <TableCell>{renderIconCell(row.icon)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.created_at ?? '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default StatusList;
